import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Row, Container, Form, Label, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { Breadcrumb, BreadcrumbItem } from "reactstrap";
import FormInput from "../../Common/Form/FormInput";
import FormTextarea from "../../Common/Form/FormTextarea";
import FormSelect from "../../Common/Form/FormSelect";
import FormUpload from "../../Common/Form/FormUpload";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { CustomLink } from "src/components/Common/CustomLink";
import APP_ROUTES from "src/routes/routes";
import { FORM_TYPE, SIMULATION_TYPE, EXECUTION_FILE_TYPE, UPLOAD_TYPE, FILE_TYPE, SIMULATION_TYPE_OPTIONS } from "src/constant/common";
import {
  handleScrollTo,
  transformDataVersionsToOptions,
  mappingPrefixInSeriesData,
  isNewSimulationVersion,
  checkVersionSwitch,
} from "src/utils/common";
import { getSimulatorOptions } from "src/services/simulator-flight";
import ModalUploadFile3DOF from "src/components/Common/Modal/ModalUploadFile3DOF";
import ParamsInput3DoFTable from "src/components/Common/Table/ParamsInput3DoFTable";
import isArray from "lodash/isArray";
import { useSetRecoilState } from "recoil";
import { uploadedFilesState } from "src/recoil/atoms/uploadedFilesState";
import ParamsInput6DoFTable from "src/components/Common/Table/ParamsInput6DoFTable";
import ModalUploadFile6DOF from "src/components/Common/Modal/ModalUploadFile6DOF";
import SelectFileButton from "./SelectFileButton";
import GroupNavigateButtons from "./GroupNavigateButtons";
import isEmpty from "lodash/isEmpty";

export const FORM_NAME = {
  NAME: "name",
  VERSION: "version_name",
  TYPE: "type",
  EXECUTION: "execution_file_name",
  NOTE: "note",
  SERIES: "series",
  INPUTS: "inputs",
  PARAMS: "params",
  FILE_RESPONSE: "response",
};

const FlightSimulatorForm = ({ submitData, data, title, action, getRedirectURL = () => APP_ROUTES.HOME, errorMessages = [] }) => {
  const { t } = useTranslation("flight-simulator");
  const [toggle, setToggle] = useState(false);
  const [toggleMain6DOF, setToggleMain6DOF] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [itemDelete, setItemDelete] = useState(null);
  const [executionFiles, setExecutionFiles] = useState([]);
  const [versions, setVersions] = useState([]);
  const [isSubmit, setIsSubmit] = useState(false);
  const [isUpload, setIsUpload] = useState(false);
  const [init, setInit] = useState(true);
  let [searchParams] = useSearchParams();
  const formAction = searchParams.get("action");
  const isRetry = formAction === FORM_TYPE.EDIT;
  const setUploadedFilesState = useSetRecoilState(uploadedFilesState);
  const [seriesDataRaw, setSeriesDataRaw] = useState(data?.series || []);
  const [temporarySeriesData3DOF, setTemporarySeriesData3DOF] = useState(data?.type === SIMULATION_TYPE["3DOF"] ? data?.series || [] : []);
  const [temporarySeriesDataMain6DOF, setTemporarySeriesDataMain6DOF] = useState(
    data?.type === SIMULATION_TYPE["6DOF"] &&
      (data?.execution_file_name === EXECUTION_FILE_TYPE.MAIN_6DOF ||
        data?.execution_file_name === EXECUTION_FILE_TYPE.MCS_6DOF ||
        data?.execution_file_name === EXECUTION_FILE_TYPE.DISPERSION_6DOF ||
        data?.execution_file_name === EXECUTION_FILE_TYPE.EXPECTED_CASUALTY_6DOF)
      ? data?.series || []
      : []
  );
  const [previousVersion, setPreviousVersion] = useState(null);

  const findErrorForParamInput = (paramInput) => {
    if (!errorMessages || !Array.isArray(errorMessages)) return null;

    const parameterJsonFile = paramInput?.parameter_json?.name || paramInput?.parameter_json;
    if (!parameterJsonFile) return null;

    const matchingError = errorMessages.find((error) => {
      if (!error.series) return false;

      return error.series === parameterJsonFile;
    });

    return matchingError?.detail || null;
  };

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    getValues,
    watch,
    clearErrors,
  } = useForm({
    defaultValues: data,
  });

  const watchVersion = watch(FORM_NAME.VERSION);
  const watchType = watch(FORM_NAME.TYPE);
  const watchExecutionFile = watch(FORM_NAME.EXECUTION);
  const watchParams6dof = watch(FORM_NAME.PARAMS);
  const watchInputs6dof = watch(FORM_NAME.INPUTS);

  const isMain6DofUiFlow =
    watchType === SIMULATION_TYPE["6DOF"] &&
    (watchExecutionFile === EXECUTION_FILE_TYPE.MAIN_6DOF ||
      watchExecutionFile === EXECUTION_FILE_TYPE.DISPERSION_6DOF ||
      watchExecutionFile === EXECUTION_FILE_TYPE.MCS_6DOF ||
      watchExecutionFile === EXECUTION_FILE_TYPE.EXPECTED_CASUALTY_6DOF);

  const updateSeriesField = (series, type) => {
    return series.map((item, index) => {
      if (type === SIMULATION_TYPE["3DOF"]) {
        const { parameter_json, aero_coefficients, aero_coefficients_1, aero_coefficients_2, wind_model, thrust_profile, km_line_files, reference_trajectory_files, ...rest } = item;

        return {
          ...rest,
          serie_name: `${index + 1}`,
          serie_file_name: [parameter_json?.name || parameter_json],
          aero_files: aero_coefficients?.map((file) => file.name || file) || [],
          aero1_file: aero_coefficients_1?.name || aero_coefficients_1,
          aero2_file: aero_coefficients_2?.name || aero_coefficients_2,
          wind_model_file: wind_model?.name || wind_model,
          thrust_profile_file: thrust_profile?.name || thrust_profile,
          ...(km_line_files?.length ? { km_line_files: km_line_files.map((file) => file.name || file) } : {}),
          ...(reference_trajectory_files?.length ? { reference_trajectory_files: reference_trajectory_files.map((file) => file.name || file) } : {}),
        };
      }

      if (type === SIMULATION_TYPE["6DOF"]) {
        const {
          parameter_json,
          mass_and_shape_response,
          aero_coefficients_ascent,
          aero_coefficients_ascent_2,
          aero_coefficients_descent,
          wind_model,
          thrust_profile,
          target_mode,
          target_attitude_ascent,
          target_attitude_descent,
          target_attitude_rate_ascent,
          target_attitude_rate_descent,
          ...rest
        } = item;

        return {
          ...rest,
          serie_name: `${index + 1}`,
          serie_file_name: parameter_json?.name || parameter_json,
          mass_shape_file: mass_and_shape_response?.name || mass_and_shape_response,
          aero1_ascent_file: aero_coefficients_ascent?.name || aero_coefficients_ascent,
          aero2_ascent_file: aero_coefficients_ascent_2?.name || aero_coefficients_ascent_2,
          aero_descent_file: aero_coefficients_descent?.name || aero_coefficients_descent,
          wind_model_file: wind_model?.name || wind_model,
          thrust_profile_file: thrust_profile?.name || thrust_profile,
          is_program_rate_control: target_mode,
          target_attitude_ascent_file: target_attitude_ascent?.name || target_attitude_ascent,
          target_attitude_descent_file: target_attitude_descent?.name || target_attitude_descent,
          target_attitude_rate_ascent_file: target_attitude_rate_ascent?.name || target_attitude_rate_ascent,
          target_attitude_rate_descent_file: target_attitude_rate_descent?.name || target_attitude_rate_descent,
        };
      }
      return item;
    });
  };

  const handleSubmitForm = async (data) => {
    const type = data[FORM_NAME.TYPE];
    const executionFileType = data[FORM_NAME.EXECUTION];
    setIsSubmit(true);

    if (type === SIMULATION_TYPE["3DOF"]) {
      const series = mappingPrefixInSeriesData(seriesDataRaw, data[FORM_NAME.SERIES]);
      const updatedSeries = updateSeriesField(series, SIMULATION_TYPE["3DOF"]);
      data[FORM_NAME.SERIES] = updatedSeries;
    }
    if (type === SIMULATION_TYPE["6DOF"] && executionFileType === EXECUTION_FILE_TYPE.MAIN) {
      data[FORM_NAME.SERIES] = {
        inputs: data[FORM_NAME.INPUTS],
      };
    }
    if (
      type === SIMULATION_TYPE["6DOF"] &&
      (executionFileType === EXECUTION_FILE_TYPE.MAIN_6DOF ||
        executionFileType === EXECUTION_FILE_TYPE.MCS_6DOF ||
        executionFileType === EXECUTION_FILE_TYPE.DISPERSION_6DOF ||
        executionFileType === EXECUTION_FILE_TYPE.EXPECTED_CASUALTY_6DOF)
    ) {
      const series = mappingPrefixInSeriesData(seriesDataRaw, data[FORM_NAME.SERIES]);
      const updatedSeries = updateSeriesField(series, SIMULATION_TYPE["6DOF"]);

      data[FORM_NAME.PARAMS] = updatedSeries;
      data[FORM_NAME.SERIES] = {
        params: updatedSeries,
      };
    }
    setUploadedFilesState([]);
    return submitData({ data }, () => setIsSubmit(false));
  };

  const handleUpdateAttachmentsFile6dof = (files, fieldName, isSingleFile) => {
    if (isSingleFile) {
      setValue(fieldName, files[0]);
      return;
    }
    setValue(fieldName, files);
    if (watch(fieldName)) {
      clearErrors(fieldName);
    }
  };

  useEffect(() => {
    if (!watchType) {
      return;
    }
    const getVersions = async () => {
      const response = await getSimulatorOptions(watchType);
      setVersions(transformDataVersionsToOptions(response || []));
    };
    getVersions();
  }, [watchType]);

  useEffect(() => {
    if (!versions?.length) {
      return;
    }
    setValue(FORM_NAME.VERSION, "");
    setExecutionFiles([]);
    if (init) {
      setValue(FORM_NAME.VERSION, data[FORM_NAME.VERSION]);
    }
  }, [versions]);

  useEffect(() => {
    if (!watchVersion || !versions?.length) {
      return;
    }
    const versionFromOption = versions.find((i) => i.value === watchVersion);
    if (!versionFromOption) {
      setValue(FORM_NAME.VERSION, "");
      setInit(false);
    }
    setValue(FORM_NAME.EXECUTION, "");
    setExecutionFiles(versionFromOption?.execute_files);

    if (init && watchVersion && !previousVersion) {
      setPreviousVersion(watchVersion);
    }
  }, [watchVersion, versions]);

  useEffect(() => {
    if (!executionFiles?.length || !init || !watchVersion) {
      return;
    }
    setValue(FORM_NAME.EXECUTION, data[FORM_NAME.EXECUTION]);
    setInit(false);
  }, [executionFiles]);

  useEffect(() => {
    const firstErrorId = Object.keys(errors).length ? Object.keys(errors)[0] : null;
    if (firstErrorId) {
      handleScrollTo(0, 90, firstErrorId);
    }
  }, [errors[FORM_NAME.VERSION], errors[FORM_NAME.EXECUTION]]);

  useEffect(() => {
    const temporarySeriesData = {
      ThreeDOF: temporarySeriesData3DOF,
      Main6DoF: temporarySeriesDataMain6DOF,
    };

    const setSeriesDataWhenChangeType = () => {
      if (isMain6DofUiFlow) {
        setValue(FORM_NAME.SERIES, temporarySeriesData?.Main6DoF || []);
        setValue(FORM_NAME.PARAMS, watchParams6dof || []);
      } else if (watchType === SIMULATION_TYPE["6DOF"] && watchExecutionFile === EXECUTION_FILE_TYPE.MAIN) {
        setValue(FORM_NAME.SERIES, []);
        setValue(FORM_NAME.INPUTS, watchInputs6dof || []);
      } else if (watchType === SIMULATION_TYPE["3DOF"]) {
        setValue(FORM_NAME.SERIES, temporarySeriesData.ThreeDOF);
      }
    };

    setSeriesDataWhenChangeType();
  }, [watchType, watchExecutionFile, temporarySeriesData3DOF, temporarySeriesDataMain6DOF]);

  const handleDeleteParamsInputRow = (index) => {
    const paramsInput = getValues(FORM_NAME.SERIES);

    if (paramsInput && paramsInput?.length > index) {
      const updatedParamsInput = [...paramsInput];
      updatedParamsInput.splice(index, 1);

      setSeriesDataRaw(updatedParamsInput);
      setValue(FORM_NAME.SERIES, updatedParamsInput);

      // Update temporary series data to prevent restoration from useEffect
      if (watchType === SIMULATION_TYPE["3DOF"]) {
        setTemporarySeriesData3DOF(updatedParamsInput);
      }
      if (isMain6DofUiFlow) {
        setValue(FORM_NAME.PARAMS, updatedParamsInput);
        setTemporarySeriesDataMain6DOF(updatedParamsInput);
      }
    }

    setItemDelete(null);
    setConfirmDelete(false);
  };

  const handleModalSubmit = (uploadedFiles) => {
    const mergeSeriesData = [...getValues(FORM_NAME.SERIES), ...uploadedFiles];
    setValue(FORM_NAME.SERIES, mergeSeriesData);
    if (watchType === SIMULATION_TYPE["3DOF"]) {
      setTemporarySeriesData3DOF(mergeSeriesData);
    }

    if (isMain6DofUiFlow) {
      setValue(FORM_NAME.PARAMS, mergeSeriesData);
      setTemporarySeriesDataMain6DOF(mergeSeriesData);
      clearErrors(FORM_NAME.PARAMS);
    }
    clearErrors(FORM_NAME.SERIES);
  };

  const handleClearParamsInputData = (versionSelected) => {
    const switchResult = checkVersionSwitch(previousVersion, versionSelected);

    if (switchResult.hasSwitched) {
      setValue(FORM_NAME.SERIES, []);
      setTemporarySeriesData3DOF([]);
      setSeriesDataRaw([]);
      setUploadedFilesState([]);
    }
  };

  return (
    <Container fluid>
      <Breadcrumb>
        <BreadcrumbItem>
          <CustomLink to={getRedirectURL()} className="link-primary">
            {t("list.title")}
          </CustomLink>
        </BreadcrumbItem>
        <BreadcrumbItem active>{title || t("new.title")}</BreadcrumbItem>
      </Breadcrumb>

      <Row>
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="header-title">{title || t("new.title")}</div>
              <Form className="p-a-15">
                <div className="row">
                  <div className="col-md-6">
                    <div className="pe-md-5">
                      <FormInput
                        register={register}
                        label={t("new.name")}
                        name={FORM_NAME.NAME}
                        placeholder={t("new.name_placeholder")}
                        errors={errors?.[FORM_NAME.NAME]?.message}
                        requiredMessage={t("new.name_required")}
                        required
                        maxLength={255}
                        maxLengthMessage={t("new.name_maxlength")}
                      />
                      <FormTextarea
                        register={register}
                        label={t("new.note")}
                        name={FORM_NAME.NOTE}
                        placeholder={t("new.note_placeholder")}
                        classInput="height-by-px-129"
                        rows={4}
                        errors={errors?.[FORM_NAME.NOTE]?.message}
                        maxLength={2000}
                        maxLengthMessage={t("new.note_maxlength")}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="ps-md-5">
                      <FormSelect
                        label={t("new.type")}
                        name={FORM_NAME.TYPE}
                        placeholder={t("new.type_placeholder")}
                        control={control}
                        errors={errors?.[FORM_NAME.TYPE]?.message}
                        required
                        options={Object.keys(SIMULATION_TYPE_OPTIONS).map((key) => ({
                          label: SIMULATION_TYPE_OPTIONS[key],
                          value: SIMULATION_TYPE_OPTIONS[key],
                        }))}
                        requiredMessage={t("new.type_required")}
                        onChangeExtra={(type) => {
                          if (type !== watchType) {
                            setValue(FORM_NAME.VERSION, "");
                          }
                        }}
                      />
                      <FormSelect
                        label={t("new.version")}
                        name={FORM_NAME.VERSION}
                        placeholder={t("new.version_placeholder")}
                        control={control}
                        errors={errors?.[FORM_NAME.VERSION]?.message}
                        required
                        options={versions}
                        requiredMessage={t("new.version_required")}
                        onChangeExtra={(versionSelected) => {
                          if (watchType === SIMULATION_TYPE["3DOF"]) {
                            handleClearParamsInputData(versionSelected);
                            setPreviousVersion(versionSelected);
                          }
                        }}
                      />
                      <FormSelect
                        label={t("new.execution")}
                        name={FORM_NAME.EXECUTION}
                        placeholder={t("new.execution_placeholder")}
                        control={control}
                        errors={errors?.[FORM_NAME.EXECUTION]?.message}
                        required
                        options={executionFiles}
                        watch={watch(FORM_NAME.EXECUTION)}
                        requiredMessage={t("new.execution_required")}
                      />
                    </div>
                  </div>
                  {watchType === SIMULATION_TYPE["3DOF"] && !isEmpty(versions) && !isEmpty(watchVersion) ? (
                    <>
                      <input
                        type="hidden"
                        name={FORM_NAME.SERIES}
                        {...register(FORM_NAME.SERIES, { required: t("new.parameter_json_required") })}
                      />
                      <div className="col-12 mb-4">
                        <Label className="position-relative required-input">{t("new.params_input")}</Label>
                        <ParamsInput3DoFTable
                          paramsInput={isArray(getValues(FORM_NAME.SERIES)) ? getValues(FORM_NAME.SERIES) : []}
                          setConfirmDelete={setConfirmDelete}
                          setItemDelete={setItemDelete}
                          error={errors?.[FORM_NAME.SERIES]?.message}
                          isNewVersion={isNewSimulationVersion(watchVersion)}
                          findErrorForParamInput={findErrorForParamInput}
                          isDebrisImpact={watchExecutionFile === EXECUTION_FILE_TYPE.DEBRIS_IMPACT}
                        />
                        <SelectFileButton setToggle={setToggle} disabled={isSubmit} />
                        {errors?.[FORM_NAME.SERIES]?.message ? (
                          <div className="text-danger font-12 position-absolute">{errors[FORM_NAME.SERIES].message}</div>
                        ) : null}
                      </div>
                    </>
                  ) : null}

                  {watchType === SIMULATION_TYPE["6DOF"] && watchExecutionFile === EXECUTION_FILE_TYPE.MAIN ? (
                    <div className="col-md-12 mb-4">
                      <Label className="position-relative required-input">{t("new.input_series")}</Label>
                      <FormUpload
                        register={register}
                        validate={(files) => files.length > 0 || t("new.required_attachments")}
                        errors={errors}
                        files={watchInputs6dof}
                        name={FORM_NAME.INPUTS}
                        fileTypeAllow={[FILE_TYPE.JSON, FILE_TYPE.CSV]}
                        fileTypeValidateMsg="new.validate_json_csv_attachments"
                        uploadType={UPLOAD_TYPE["6DOF"]}
                        handleUpdateAttachments={(files) => handleUpdateAttachmentsFile6dof(files, FORM_NAME.INPUTS)}
                        useNewApiUpload
                        onUpload={setIsUpload}
                      />
                    </div>
                  ) : null}

                  {isMain6DofUiFlow ? (
                    <>
                      <input
                        type="hidden"
                        name={FORM_NAME.PARAMS}
                        {...register(FORM_NAME.PARAMS, { required: t("new.parameter_json_required") })}
                      />
                      <div className="col-md-12 mb-4">
                        <Label className="position-relative required-input">{t("new.params_input")}</Label>
                        <ParamsInput6DoFTable
                          paramsInput={isArray(getValues(FORM_NAME.SERIES)) ? getValues(FORM_NAME.SERIES) : []}
                          setConfirmDelete={setConfirmDelete}
                          setItemDelete={setItemDelete}
                        />
                        <SelectFileButton setToggle={setToggleMain6DOF} disabled={isSubmit} />
                        {errors?.[FORM_NAME.PARAMS]?.message ? (
                          <div className="text-danger font-12 position-absolute">{errors[FORM_NAME.PARAMS].message}</div>
                        ) : null}
                      </div>
                    </>
                  ) : null}
                </div>

                <div className="d-flex justify-content-center gap-2">
                  <GroupNavigateButtons
                    handleSubmitForm={handleSubmit((data) => handleSubmitForm(data))}
                    ownerId={isRetry ? data?.created_by : null}
                    disabled={isSubmit || isUpload}
                    isSubmit={isSubmit}
                    isRetry={isRetry}
                    getRedirectURL={getRedirectURL}
                  />
                </div>
              </Form>
            </div>
          </div>
        </div>
      </Row>

      <Modal isOpen={confirmDelete} toggle={() => setConfirmDelete(false)} backdrop="static">
        <ModalHeader>{t("new.delete_modal.title_attachments")}</ModalHeader>
        <ModalBody>
          <div>{t("new.delete_modal.sure")}</div>
          <div>{t("new.delete_modal.recover")}</div>
        </ModalBody>
        <ModalFooter>
          <span className="btn btn-secondary btn-sm" role="button" onClick={() => setConfirmDelete(false)}>
            {t("new.delete_modal.cancel")}
          </span>
          <span className="btn btn-danger btn-sm" role="button" onClick={() => handleDeleteParamsInputRow(itemDelete)}>
            {t("new.delete_modal.delete")}
          </span>
        </ModalFooter>
      </Modal>

      <ModalUploadFile3DOF
        paramsInput={getValues(FORM_NAME.SERIES)}
        setToggle={setToggle}
        toggle={toggle}
        onModalSubmit={handleModalSubmit}
        isNewVersion={isNewSimulationVersion(watchVersion)}
        isDebrisImpact={watchExecutionFile === EXECUTION_FILE_TYPE.DEBRIS_IMPACT}
      />
      <ModalUploadFile6DOF
        paramsInput={getValues(FORM_NAME.SERIES)}
        setToggle={setToggleMain6DOF}
        toggle={toggleMain6DOF}
        onModalSubmit={handleModalSubmit}
      />
    </Container>
  );
};

export default FlightSimulatorForm;
